import { pathOr } from "ramda";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";

import CommonTable from "../../../../components/commonTable";
import LoadingSpinner from "../../../../components/loadingSpinner";
import CustomNotification from "../../../../components/notification";
import emailEditorService from "../../../../service/emailEditorService";
import loginService from "../../../../service/login";
import CreateEmailTemplate from "../emailEditor/CreateEmailTemplate";
import { templateTableColumns } from "./constants";
import { setPopup } from "../../../../actions/popupAction";
import ConfirmPopup from "../../../../components/confirmPopup";
import CustomButton from "../../../../components/customButton";
import CommonService from "../../../../service/CommonService";
import UserService from "../../../../service/UserService";

import "./styles.scss";

const commonservice = new CommonService();

function Templates(props) {
  const practiceId = loginService.getPracticeId();
  const dispatch = useDispatch();
  const userservice = new UserService();

  const [state, setState] = useState({
    loading: false,
    templateId: null,
    emailTemplatesList: [],
    showTemplate: false,
    deleteTemplateModal: false,
    deleteTemplateData: {},
    emailTemplateData: props?.emailTemplateData,
    accountsList: [],
    allAccountsList: [],
  });

  const { accountsList, allAccountsList } = state;
  const { editTemplate = undefined } = state.emailTemplateData;
  const { customPopupMessage, messageType, message } = useSelector(
    (state) => state.popupReducer
  );
  const currentUser = useSelector((state) => state.login.user);
  const isTemplateModificationAllowed = ["OU", "TS"].includes(
    currentUser?.roles?.roleKey
  );
  const getEmailTemplates = async () => {
    try {
      setState((st) => ({
        ...st,
        loading: true,
      }));
      const {
        data: emailTemplatesList,
      } = await emailEditorService.getEmailEditor(practiceId);

      setState((st) => ({
        ...st,
        emailTemplatesList,
      }));
    } catch (err) {
      dispatch(
        setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: pathOr(
            "Something went wrong",
            ["response", "data", "message"],
            err
          ),
        })
      );
    } finally {
      setState((st) => ({
        ...st,
        loading: false,
      }));
    }
  };

  useEffect(() => {
    getEmailTemplates();
  }, []);

  const handleSaveTemplate = (type) => {
    setState((st) => ({
      ...st,
      showTemplate: false,
      emailTemplateData: {},
      templateId: null,
    }));

    dispatch(
      setPopup({
        customPopupMessage: true,
        messageType: "success",
        message: `Template has been ${
          type === "save" ? "created" : "updated"
        } successfully.`,
      })
    );
    getEmailTemplates();
  };

  const handleEditTemplate = (record) => {
    setState((st) => ({ ...st, showTemplate: true, templateId: record.id }));
  };

  const deleteTemplate = async () => {
    try {
      setState((st) => ({
        ...st,
        loading: true,
      }));
      await commonservice.deleteEmailTemplate(state.deleteTemplateData.id);
      dispatch(
        setPopup({
          customPopupMessage: true,
          messageType: "success",
          message: "Template has been deleted successfully.",
        })
      );
    } catch (err) {
      dispatch(
        setPopup({
          customPopupMessage: true,
          messageType: "error",
          message: pathOr(
            "Something went wrong",
            ["response", "data", "message"],
            err
          ),
        })
      );
    } finally {
      setState((st) => ({
        ...st,
        loading: false,
        deleteTemplateModal: false,
      }));
      getEmailTemplates();
    }
  };

  const handleDeletePopup = (record) => {
    setState((st) => ({
      ...st,
      deleteTemplateModal: !state.deleteTemplateModal,
      deleteTemplateData: record,
    }));
  };

  const fetchAccounts = (page = 1, searchQuery) => {
    return new Promise(async (resolve, reject) => {
      try {
        let accountsList = [];
        let count = 0;
        const responseData = await userservice.getPortalAccountsForAdmin(
          page,
          searchQuery
        );
        accountsList = responseData.rows;
        count = responseData.count;

        setState((st) => ({
          ...st,
          accountsList: accountsList,
          allAccountsList: !searchQuery ? accountsList : st.allAccountsList,
        }));
        resolve(count);
      } catch (err) {
        reject(err);
        setPopup({
          customPopupMessage: true,
          message: pathOr(
            "Internal Server Error. Please check the connection",
            ["response", "data", "message"],
            err
          ),
          messageType: "error",
        });
      }
    });
  };

  return (
    <>
      {customPopupMessage && (
        <CustomNotification messageType={messageType} message={message} />
      )}
      {state.deleteTemplateModal && (
        <ConfirmPopup
          isDelete
          titleMessage="Confirm to Delete"
          handleCancel={() =>
            setState((st) => ({
              ...st,
              deleteTemplateModal: !state.deleteTemplateModal,
            }))
          }
          handleDelete={() => deleteTemplate()}
          isVisible={state.deleteTemplateModal}
          submitButtonText="Delete"
          cancelButtonText="Cancel"
          message={
            <>
              <div>
                Are you sure you want to delete{" "}
                <strong> {state.deleteTemplateData.name} </strong>
                from saved templates?
              </div>
              {state.deleteTemplateData?.showAs === "default" && (
                <em>
                  Deleting this default template will also remove it for all
                  other accounts.
                </em>
              )}
            </>
          }
        />
      )}
      <div className="template-main-container">
        {state.loading && (
          <>
            <div className="hide-for-mob">
              <LoadingSpinner customClassName="broadcast" />
            </div>
            <div className="display-in-mob">
              <LoadingSpinner customClassName="broadcast" type="large" />
            </div>
          </>
        )}

        {editTemplate === undefined && !state.showTemplate ? (
          <>
            <div className="email-template-header">
              <h2>Email Templates</h2>
              <CustomButton
                title="Create new template"
                onClick={() =>
                  setState((st) => ({ ...st, showTemplate: true }))
                }
                type="primary"
              />
            </div>
            <CommonTable
              columns={templateTableColumns(
                handleEditTemplate,
                handleDeletePopup,
                isTemplateModificationAllowed
              )}
              tableData={{
                rows: state.emailTemplatesList,
                count: state.emailTemplatesList.length,
              }}
              reportHeader={false}
            />
          </>
        ) : (
          <Formik
            initialValues={{
              accounts: [],
            }}
          >
            {({ setFieldValue, values }) => (
              <CreateEmailTemplate
                mergeTags={[]}
                templateId={state.templateId}
                {...state.emailTemplateData}
                practiceId={practiceId}
                onBack={() =>
                  setState((st) => ({
                    ...st,
                    showTemplate: false,
                    templateId: null,
                    emailTemplateData: {},
                  }))
                }
                onSave={() => handleSaveTemplate("save")}
                onUpdate={() => handleSaveTemplate("update")}
                isTemplateModificationAllowed={isTemplateModificationAllowed}
                fetchAccounts={fetchAccounts}
                accountsList={accountsList}
                allAccountsList={allAccountsList}
                formValues={values}
                setFieldValue={setFieldValue}
              />
            )}
          </Formik>
        )}
      </div>
    </>
  );
}

export default Templates;
